export enum AdChannel {
  META = "Meta",
  TIKTOK = "Tiktok",
  GOOGLE_ADS = "Google Ads",
  YOUTUBE = "YouTube",
  PINTEREST = "Pinterest",
}

export enum InitiativeType {
  DEMAND_CAPTURE_ACQUISITION = "DEMAND_CAPTURE_ACQUISITION",
  DEMAND_CAPTURE_RETARGETING = "DEMAND_CAPTURE_RETARGETING",
  DEMAND_CAPTURE_BROAD = "DEMAND_CAPTURE_BROAD",
  DEMAND_GENERATION = "DEMAND_GENERATION",
  DEMAND_GENERATION_2 = "DEMAND_GENERATION_2",
  RETENTION = "RETENTION",
  BRANDED_SEARCH = "BRANDED_SEARCH",
  NON_BRAND_SEARCH = "NON_BRAND_SEARCH",
  PMAX = "PMAX",
}

export enum MetaObjective {
  OUTCOME_SALES = "OUTCOME_SALES",
  OUTCOME_TRAFFIC = "OUTCOME_TRAFFIC",
  LINK_CLICKS = "LINK_CLICKS",
  OUTCOME_ENGAGEMENT = "OUTCOME_ENGAGEMENT",
  CONVERSIONS = "CONVERSIONS",
  PRODUCT_CATALOG_SALES = "PRODUCT_CATALOG_SALES",
  OUTCOME_AWARENESS = "OUTCOME_AWARENESS",
  REACH = "REACH",
  BRAND_AWARENESS = "BRAND_AWARENESS",
}

export enum TiktokGoal {
  REACH = "REACH",
  TRAFFIC = "TRAFFIC",
  VIDEO_VIEWS = "VIDEO_VIEWS",
  LEAD_GENERATION = "LEAD_GENERATION",
  ENGAGEMENT = "ENGAGEMENT",
  APP_PROMOTION = "APP_PROMOTION",
  WEB_CONVERSIONS = "WEB_CONVERSIONS",
  PRODUCT_SALES = "PRODUCT_SALES",
  CLICK = "CLICK",
  ENGAGED_VIEW_FIFTEEN = "ENGAGED_VIEW_FIFTEEN",
  VALUE = "VALUE",
  ENGAGED_VIEW = "ENGAGED_VIEW",
  TRAFFIC_LANDING_PAGE_VIEW = "TRAFFIC_LANDING_PAGE_VIEW",
  INSTALL = "INSTALL",
  CONVERT = "CONVERT",
  FOLLOWERS = "FOLLOWERS",
  PAGE_VISIT = "PAGE_VISIT",
  PROFILE_VIEWS = "PROFILE_VIEWS",
  VIDEO_VIEW = "VIDEO_VIEW",
  MT_LIVE_ROOM = "MT_LIVE_ROOM",
}

/**
 * Pinterest ad-group optimization goals as pipeline-dbt derives them: the
 * conversion event for conversion ad groups (CHECKOUT, ADD_TO_CART, SIGNUP,
 * LEAD, ...), else the billable event (IMPRESSION, CLICKTHROUGH,
 * VIDEO_V_50_MRC). Campaign objective is NULL for Pinterest rows, like TikTok.
 * Must match the API's PinterestGoal, which leaves out PAGE_VISIT until the
 * pipeline's model-to-ad-set join matches the channel (a Pinterest PAGE_VISIT
 * model would otherwise also apply to TikTok PAGE_VISIT ad groups).
 */
export enum PinterestGoal {
  CHECKOUT = "CHECKOUT",
  ADD_TO_CART = "ADD_TO_CART",
  SIGNUP = "SIGNUP",
  LEAD = "LEAD",
  IMPRESSION = "IMPRESSION",
  CLICKTHROUGH = "CLICKTHROUGH",
  VIDEO_V_50_MRC = "VIDEO_V_50_MRC",
}

export enum MetaOptimizationGoal {
  RETURN_ON_AD_SPEND = "RETURN_ON_AD_SPEND",
  LANDING_PAGE_VIEWS = "LANDING_PAGE_VIEWS",
  LINK_CLICKS = "LINK_CLICKS",
  OFFSITE_CONVERSIONS = "OFFSITE_CONVERSIONS",
  PAGE_LIKES = "PAGE_LIKES",
  POST_ENGAGEMENT = "POST_ENGAGEMENT",
  REACH = "REACH",
  THRUPLAY = "THRUPLAY",
  PROFILE_VISIT = "PROFILE_VISIT",
  AD_RECALL_LIFT = "AD_RECALL_LIFT",
  REPLIES = "REPLIES",
  VISIT_INSTAGRAM_PROFILE = "VISIT_INSTAGRAM_PROFILE",
  IMPRESSIONS = "IMPRESSIONS",
  LEAD_GENERATION = "LEAD_GENERATION",
  QUALITY_LEAD = "QUALITY_LEAD",
  APP_INSTALLS = "APP_INSTALLS",
  VALUE = "VALUE",
  CONVERSATIONS = "CONVERSATIONS",
  TWO_SECOND_CONTINUOUS_VIDEO_VIEWS = "TWO_SECOND_CONTINUOUS_VIDEO_VIEWS",
  EVENT_RESPONSES = "EVENT_RESPONSES",
  REMINDERS_SET = "REMINDERS_SET",
  ONSITE_CONVERSIONS = "ONSITE_CONVERSIONS",
  PROFILE_AND_PAGE_ENGAGEMENT = "PROFILE_AND_PAGE_ENGAGEMENT",
}

export enum GoogleAdsOptimizationGoal {
  SEARCH = "SEARCH",
  DISPLAY = "DISPLAY",
  VIDEO = "VIDEO",
  SHOPPING = "SHOPPING",
  PERFORMANCE_MAX = "PERFORMANCE_MAX",
  DISCOVERY = "DISCOVERY",
  HOTEL = "HOTEL",
  MULTI_CHANNEL = "MULTI_CHANNEL",
}

// Union type for optimization goals (Meta, TikTok, Pinterest, Google Ads/YouTube)
export type OptimizationGoal =
  | MetaOptimizationGoal
  | TiktokGoal
  | PinterestGoal
  | GoogleAdsOptimizationGoal

// --- Weight / metric enums (must match backend DTOs exactly) ---

export enum AttributionSource {
  GOOGLE_ANALYTICS = "GOOGLE_ANALYTICS",
  GOOGLE_ADS = "GOOGLE_ADS",
  TIKTOK = "TIKTOK",
  META = "META",
  MARATHON = "MARATHON",
  SHOPIFY = "SHOPIFY",
  YOUTUBE_ADS = "YOUTUBE_ADS",
  PINTEREST = "PINTEREST",
}

/** Triple Whale attribution metrics; values must match DBT exactly. */
export enum TripleWhaleMetric {
  TRIPLE_WHALE_LINEAR = "TRIPLE_WHALE_LINEAR",
  TRIPLE_WHALE_LAST_PLATFORM_CLICK = "TRIPLE_WHALE_LAST_PLATFORM_CLICK",
  TRIPLE_WHALE_FULL_FIRST_CLICK = "TRIPLE_WHALE_FULL_FIRST_CLICK",
  TRIPLE_WHALE_FULL_LAST_CLICK = "TRIPLE_WHALE_FULL_LAST_CLICK",
  TRIPLE_WHALE_LINEAR_ALL = "TRIPLE_WHALE_LINEAR_ALL",
  TRIPLE_WHALE_LINEAR_NC = "TRIPLE_WHALE_LINEAR_NC",
  TRIPLE_WHALE_LAST_PLATFORM_CLICK_NC = "TRIPLE_WHALE_LAST_PLATFORM_CLICK_NC",
  TRIPLE_WHALE_FULL_FIRST_CLICK_NC = "TRIPLE_WHALE_FULL_FIRST_CLICK_NC",
  TRIPLE_WHALE_FULL_LAST_CLICK_NC = "TRIPLE_WHALE_FULL_LAST_CLICK_NC",
  TRIPLE_WHALE_LINEAR_ALL_NC = "TRIPLE_WHALE_LINEAR_ALL_NC",
}

export enum GoogleAnalyticsMetric {
  LAST_CLICK_REVENUE = "LAST_CLICK_REVENUE",
}

export enum GoogleAdsMetric {
  SEVEN_DAY_CLICK_1D_VIEW = "SEVEN_DAY_CLICK_1D_VIEW",
}

export enum TiktokMetric {
  SEVEN_DAY_CLICK_1D_VIEW = "SEVEN_DAY_CLICK_1D_VIEW",
}

/** Pinterest attribution windows; values must match DBT (`PINTEREST` weight source) exactly. */
export enum PinterestMetric {
  THIRTY_DAY_CLICK_30D_ENGAGEMENT_1D_VIEW = "30D_CLICK_30D_ENGAGEMENT_1D_VIEW",
  THIRTY_DAY_CLICK = "30D_CLICK",
  THIRTY_DAY_ENGAGEMENT = "30D_ENGAGEMENT",
  ONE_DAY_VIEW = "1D_VIEW",
}

export enum MetaMetric {
  ONE_DAY_ENGAGED_VIEW = "1D_ENGAGED_VIEW",
  SEVEN_DAY_CLICK = "7D_CLICK",
  ONE_DAY_VIEW = "1D_VIEW",
  ONE_DAY_CLICK = "1D_CLICK",
  INCREMENTALITY = "INCREMENTALITY",
}

export enum MarathonMetric {
  BRAND_VALUE_30D = "BRAND_VALUE_30D",
  BRAND_VALUE_60D = "BRAND_VALUE_60D",
  BRAND_VALUE_90D = "BRAND_VALUE_90D",
  BRAND_VALUE_180D = "BRAND_VALUE_180D",
  REVENUE_FROM_SEARCH = "REVENUE_FROM_SEARCH",
}

export enum ShopifyMetric {
  LAST_CLICK_REVENUE = "LAST_CLICK_REVENUE",
  FIRST_CLICK_REVENUE = "FIRST_CLICK_REVENUE",
}
